import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SESSION_TOKEN_KEY = "spike_auth_token";
const REFRESH_INTERVAL_MS = 12000;
const DEFAULT_PAGE_SIZE = 12;

async function requestAuthedJson(path) {
  const token = window.localStorage.getItem(SESSION_TOKEN_KEY);

  if (!token) {
    throw new Error("Authorization token is required");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const responseText = await response.text();
  let payload = {};

  if (responseText) {
    try {
      payload = JSON.parse(responseText);
    } catch (error) {
      payload = {
        message: responseText,
      };
    }
  }

  if (!response.ok) {
    throw new Error(payload.message || "Request failed");
  }

  return payload;
}

function formatTimestamp(value) {
  if (!value) {
    return "--";
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(Number(value)));
  } catch (error) {
    return "--";
  }
}

function formatMetric(value, digits = 2) {
  return Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : "--";
}

function formatResultTone(result) {
  if (result === "WIN" || result === "SPIKE") {
    return "results-positive";
  }

  if (result === "LOSS" || result === "FAIL") {
    return "results-negative";
  }

  return "results-neutral";
}

function buildQueryString(params) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") {
      return;
    }

    query.set(key, String(value));
  });

  return query.toString();
}

function buildPerformanceSeries(items) {
  let score = 0;

  return [...items]
    .slice()
    .reverse()
    .map((item, index) => {
      if (item.result === "WIN" || item.result === "SPIKE") {
        score += 1;
      } else if (item.result === "LOSS" || item.result === "FAIL") {
        score -= 1;
      }

      return {
        x: index,
        y: score,
        label: item.symbol || item.market || "Trade",
      };
    });
}

function PerformanceLineChart({ items }) {
  const points = buildPerformanceSeries(items);

  if (!points.length) {
    return <div className="results-chart-empty">No stored trades yet.</div>;
  }

  const width = 520;
  const height = 220;
  const padding = 24;
  const yValues = points.map((point) => point.y);
  const minY = Math.min(...yValues, 0);
  const maxY = Math.max(...yValues, 0);
  const yRange = Math.max(1, maxY - minY);
  const xRange = Math.max(1, points.length - 1);
  const path = points
    .map((point, index) => {
      const x = padding + (index / xRange) * (width - padding * 2);
      const y = height - padding - ((point.y - minY) / yRange) * (height - padding * 2);
      return `${index === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  return (
    <svg className="results-chart-svg" viewBox={`0 0 ${width} ${height}`} role="img">
      <defs>
        <linearGradient id="results-line-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(56, 189, 248, 0.35)" />
          <stop offset="100%" stopColor="rgba(56, 189, 248, 0.02)" />
        </linearGradient>
      </defs>
      <line x1={padding} x2={width - padding} y1={height / 2} y2={height / 2} className="results-axis-line" />
      <path d={path} fill="none" stroke="#38bdf8" strokeWidth="3" strokeLinecap="round" />
      {points.map((point, index) => {
        const x = padding + (index / xRange) * (width - padding * 2);
        const y = height - padding - ((point.y - minY) / yRange) * (height - padding * 2);

        return <circle key={`${point.label}-${index}`} cx={x} cy={y} r="4" fill="#f8fafc" />;
      })}
    </svg>
  );
}

function AccuracyPieChart({ wins, losses }) {
  const total = Math.max(1, wins + losses);
  const radius = 74;
  const circumference = 2 * Math.PI * radius;
  const winLength = (wins / total) * circumference;
  const lossLength = (losses / total) * circumference;

  return (
    <div className="results-pie-wrap">
      <svg className="results-pie-svg" viewBox="0 0 220 220" role="img">
        <circle cx="110" cy="110" r={radius} className="results-pie-track" />
        <circle
          cx="110"
          cy="110"
          r={radius}
          className="results-pie-win"
          strokeDasharray={`${winLength} ${circumference - winLength}`}
          transform="rotate(-90 110 110)"
        />
        <circle
          cx="110"
          cy="110"
          r={radius}
          className="results-pie-loss"
          strokeDasharray={`${lossLength} ${circumference - lossLength}`}
          strokeDashoffset={-winLength}
          transform="rotate(-90 110 110)"
        />
      </svg>
      <div className="results-pie-center">
        <div className="results-pie-value">{wins + losses}</div>
        <div className="results-pie-label">Decided trades</div>
      </div>
    </div>
  );
}

export default function ResultsDashboard({
  user,
  onLogout,
  onNavigateLive,
  onNavigateResults,
  onNavigateRadar,
  onNavigateAutoTrader,
}) {
  const [scope, setScope] = useState("all");
  const [filters, setFilters] = useState({
    pair: "",
    timeframe: "",
    dateFrom: "",
    dateTo: "",
  });
  const [sort, setSort] = useState({
    column: "evaluatedAt",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [resultsState, setResultsState] = useState({
    items: [],
    pagination: {
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
      total: 0,
      totalPages: 0,
    },
  });
  const [accuracy, setAccuracy] = useState({
    totalTrades: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    avgRr: null,
  });
  const [chartItems, setChartItems] = useState([]);
  const [latestTrade, setLatestTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const queryString = useMemo(
    () =>
      buildQueryString({
        scope,
        ...filters,
        page,
        limit: DEFAULT_PAGE_SIZE,
        sortBy: sort.column,
        sortDirection: sort.direction,
      }),
    [filters, page, scope, sort]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadDashboardData() {
      setLoading(true);
      setError("");

      try {
        const [resultsPayload, accuracyPayload, latestTradePayload, chartPayload] = await Promise.all([
          requestAuthedJson(`/api/trade-results?${queryString}`),
          requestAuthedJson(
            `/api/trade-results/accuracy?${buildQueryString({
              scope,
              ...filters,
            })}`
          ),
          requestAuthedJson(
            `/api/trade-results?${buildQueryString({
              scope,
              ...filters,
              page: 1,
              limit: 1,
              sortBy: "evaluatedAt",
              sortDirection: "desc",
            })}`
          ),
          requestAuthedJson(
            `/api/trade-results?${buildQueryString({
              scope,
              ...filters,
              page: 1,
              limit: 100,
              sortBy: "evaluatedAt",
              sortDirection: "desc",
            })}`
          ),
        ]);

        if (cancelled) {
          return;
        }

        setResultsState(resultsPayload);
        setAccuracy(accuracyPayload);
        setLatestTrade(latestTradePayload?.items?.[0] || null);
        setChartItems(chartPayload?.items || []);
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError.message || "Failed to load saved results.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDashboardData();
    const intervalId = window.setInterval(loadDashboardData, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [filters, queryString, scope]);

  function handleSort(column) {
    setPage(1);
    setSort((currentSort) => ({
      column,
      direction:
        currentSort.column === column && currentSort.direction === "desc" ? "asc" : "desc",
    }));
  }

  function handleFilterChange(key, value) {
    setPage(1);
    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
  }

  const scopeLabel = scope === "mine" ? "My Results" : "All Users";
  const totalTradesLabel = scope === "mine" ? "My Trades" : "System Trades";
  const lastTradeLabel = scope === "mine" ? "My Last Result" : "Latest System Result";

  return (
    <div className="results-dashboard-shell">
      <div className="results-dashboard-header">
        <div>
          <div className="results-kicker">Results Intelligence</div>
          <h1 className="results-title">Saved Performance Dashboard</h1>
          <p className="results-subtitle">
            Shared spike results with system-wide accuracy and user-level filtering.
          </p>
        </div>
        <div className="results-header-actions">
          <div className="results-nav">
            <button type="button" className="results-nav-button" onClick={onNavigateLive}>
              Live Workspace
            </button>
            <button type="button" className="results-nav-button results-nav-button-active" onClick={onNavigateResults}>
              Saved Results
            </button>
            <button type="button" className="results-nav-button" onClick={onNavigateRadar}>
              Radar
            </button>
            <button type="button" className="results-nav-button" onClick={onNavigateAutoTrader}>
              Auto Trader
            </button>
          </div>
          <div className="results-session-card">
            <div className="results-session-copy">
              <span className="results-session-kicker">Signed In</span>
              <strong>{user?.full_name || user?.email || "Spike User"}</strong>
            </div>
            <button type="button" className="results-logout-button" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="results-filter-bar">
        <select
          className="results-filter-input"
          value={scope}
          onChange={(event) => {
            setPage(1);
            setScope(event.target.value);
          }}
        >
          <option value="all">All Users</option>
          <option value="mine">My Results</option>
        </select>
        <input
          className="results-filter-input"
          placeholder="Pair e.g. Boom 1000"
          value={filters.pair}
          onChange={(event) => handleFilterChange("pair", event.target.value)}
        />
        <input
          className="results-filter-input"
          placeholder="Timeframe seconds"
          value={filters.timeframe}
          onChange={(event) => handleFilterChange("timeframe", event.target.value)}
        />
        <input
          className="results-filter-input"
          type="date"
          value={filters.dateFrom}
          onChange={(event) => handleFilterChange("dateFrom", event.target.value)}
        />
        <input
          className="results-filter-input"
          type="date"
          value={filters.dateTo}
          onChange={(event) => handleFilterChange("dateTo", event.target.value)}
        />
      </div>

      {error ? <div className="results-error-banner">{error}</div> : null}

      <div className="results-summary-grid">
        <article className="results-summary-card">
          <span className="results-card-label">{totalTradesLabel}</span>
          <strong className="results-card-value">{accuracy.totalTrades}</strong>
        </article>
        <article className="results-summary-card">
          <span className="results-card-label">Win Rate</span>
          <strong className="results-card-value">{formatMetric(accuracy.winRate)}%</strong>
        </article>
        <article className="results-summary-card">
          <span className="results-card-label">Wins / Losses</span>
          <strong className="results-card-value">
            {accuracy.wins} / {accuracy.losses}
          </strong>
        </article>
        <article className="results-summary-card">
          <span className="results-card-label">{lastTradeLabel}</span>
          <strong className={`results-card-value ${formatResultTone(latestTrade?.result)}`}>
            {latestTrade?.result || "--"}
          </strong>
        </article>
      </div>

      <div className="results-visual-grid">
        <section className="results-panel">
          <div className="results-panel-header">
            <h2>Performance Trend</h2>
            <span>{scopeLabel} • Auto refresh every 12s</span>
          </div>
          <PerformanceLineChart items={chartItems} />
        </section>
        <section className="results-panel">
          <div className="results-panel-header">
            <h2>Win vs Loss</h2>
            <span>{scopeLabel} • Avg RR {formatMetric(accuracy.avgRr)}</span>
          </div>
          <AccuracyPieChart wins={accuracy.wins} losses={accuracy.losses} />
          <div className="results-pie-legend">
            <span><i className="results-legend-dot results-legend-dot-win" /> Wins</span>
            <span><i className="results-legend-dot results-legend-dot-loss" /> Losses</span>
          </div>
        </section>
      </div>

      <section className="results-panel results-table-panel">
        <div className="results-panel-header">
          <h2>{scopeLabel} Results</h2>
          <span>
            Page {resultsState.pagination.page} of {Math.max(1, resultsState.pagination.totalPages || 1)}
          </span>
        </div>

        <div className="results-table-wrap">
          <table className="results-table">
            <thead>
              <tr>
                <th><button type="button" onClick={() => handleSort("pair")}>Pair</button></th>
                <th><button type="button" onClick={() => handleSort("timeframe")}>Timeframe</button></th>
                <th><button type="button" onClick={() => handleSort("zoneType")}>Zone Type</button></th>
                <th><button type="button" onClick={() => handleSort("entryPrice")}>Entry</button></th>
                <th><button type="button" onClick={() => handleSort("result")}>Result</button></th>
                <th><button type="button" onClick={() => handleSort("rr")}>RR</button></th>
                <th><button type="button" onClick={() => handleSort("savedByCount")}>Saves</button></th>
                <th><button type="button" onClick={() => handleSort("evaluatedAt")}>Timestamp</button></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="results-table-empty">Loading saved results...</td>
                </tr>
              ) : resultsState.items.length ? (
                resultsState.items.map((item) => (
                  <tr key={item.tradeKey}>
                    <td>{item.symbol || "--"}</td>
                    <td>{item.timeframe ? `${item.timeframe}s` : "--"}</td>
                    <td>{item.zoneType || "--"}</td>
                    <td>{formatMetric(item.entryPrice)}</td>
                    <td className={formatResultTone(item.result)}>{item.result || "--"}</td>
                    <td>{formatMetric(item.rr)}</td>
                    <td>{item.savedByCount || 0}</td>
                    <td>{formatTimestamp(item.evaluatedAt || item.signalTimestamp)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="results-table-empty">No results match the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="results-pagination">
          <button
            type="button"
            className="results-page-button"
            disabled={page <= 1}
            onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
          >
            Previous
          </button>
          <span>
            {resultsState.pagination.total} records
          </span>
          <button
            type="button"
            className="results-page-button"
            disabled={page >= Math.max(1, resultsState.pagination.totalPages || 1)}
            onClick={() => setPage((currentPage) => currentPage + 1)}
          >
            Next
          </button>
        </div>
      </section>
    </div>
  );
}
